import { getGACookieFingerprint, getGAIdentifiers } from '@brands/hooks/useGoogleSession';
import axios, { AxiosHeaders, InternalAxiosRequestConfig } from 'axios';
import axiosRetry from 'axios-retry';
import Cookies from 'js-cookie';

import { getGoogleClickIdHeaders } from '../../../googleAttribution';
import { config } from '../config/config';
import cleanPayload from '../Utils/cleanPayload';
import { cookieOptions, jwtToken } from '../Utils/getToken';

function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (value === null || typeof value !== 'object') return false;
  const proto = Object.getPrototypeOf(value);
  return proto === null || proto === Object.prototype;
}

let gaHeaders: Record<string, string> = {};
let gaHeadersPromise: Promise<void> | null = null;
let gaCookieFingerprint = '';

// Build the GA attribution headers, omitting any identifier that isn't a
// non-empty string. Sending a blank `x-google-*` header is worse than sending
// nothing: the backend treats the header as present-but-empty and the event is
// recorded unattributed. Only attach headers we actually have values for.
const buildGAHeaders = (): Record<string, string> => {
  const { clientId, sessionId, sessionNumber } = getGAIdentifiers();

  const headers: Record<string, string> = {};
  if (clientId) headers['x-google-client-id'] = clientId;
  if (sessionId) headers['x-google-session-id'] = sessionId;
  if (sessionNumber) headers['x-google-session-number'] = sessionNumber;

  return headers;
};

// Refresh the cached GA headers from the current cookies when needed.
//
// GA4 writes its `_ga` / `_ga_<container>` cookies asynchronously, so the very
// first API call of a session can run before any identifier exists. We must not
// treat that empty read as a completed sync: if we did, the "no cookie"
// fingerprint would stay cached and we'd never re-read once GA4 initialized,
// leaving the rest of the session unattributed. So we only mark the sync as
// satisfied (cache the fingerprint) once we've actually captured at least one
// identifier; until then every request re-reads the cookies.
export const syncGAHeaders = (): Promise<void> => {
  const nextFingerprint = getGACookieFingerprint();
  const hasCachedHeaders = Object.keys(gaHeaders).length > 0;
  const fingerprintChanged = gaCookieFingerprint !== nextFingerprint;

  if (hasCachedHeaders && !fingerprintChanged) {
    return Promise.resolve();
  }

  if (gaHeadersPromise) {
    return gaHeadersPromise;
  }

  gaHeadersPromise = (async () => {
    try {
      const nextHeaders = buildGAHeaders();
      gaHeaders = nextHeaders;
      // Only cache the fingerprint once we have real identifiers. An empty read
      // leaves the fingerprint stale so the next request re-attempts the sync.
      gaCookieFingerprint = Object.keys(nextHeaders).length > 0 ? nextFingerprint : '';
    } catch (err) {
      console.warn('Failed to get GA identifiers', err);
      gaHeaders = {};
      gaCookieFingerprint = '';
    }
  })().finally(() => {
    gaHeadersPromise = null;
  });

  return gaHeadersPromise;
};

// Tenant headers (x-organization-id / x-division-id / x-practice-id) are
// intentionally NOT declared as axios.create() defaults. Axios v1 stores
// `create({ headers })` under `defaults.headers.common` and then merges them
// with method-scoped defaults (`defaults.headers.get`, `.post`, `.patch`, …)
// at request time. For body methods (POST/PATCH/PUT) the merge drops the
// common `x-division-id` entry silently — a PATCH from this client reaches
// the wire without `x-division-id`, while a GET from the same instance
// carries it. The backend's Patient auth branch requires either
// `x-division-id` or `x-practice-id`, so the dropped header surfaces as
// `401 {"detail":"Invalid organization"}`. Set the headers in the request
// interceptor below so every request — regardless of HTTP method — gets
// them attached after axios has finished its merge step.
export const client = axios.create({
  baseURL: config.api.baseUrl,
  headers: {
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  },
});

// Mirrors the dentistry-one client. Restrict auto-retry to network
// errors and idempotent-method 5xx/429s — `isRetryableError` retried
// every 5xx including POST/PATCH, which let a slow-but-successful
// create-user request get duplicated by the retry. See the comment in
// `src/brands/dentistry-one/services/client.ts` for the full trace.
axiosRetry(client, {
  retries: 1,
  retryDelay: axiosRetry.exponentialDelay,
  retryCondition: axiosRetry.isNetworkOrIdempotentRequestError,
});

client.interceptors.request.use(
  async (request) => {
    await syncGAHeaders();

    const headers = AxiosHeaders.from(request.headers);

    // Re-attach tenant headers here, after axios has merged its per-method
    // defaults. See the comment on `axios.create()` above.
    if (config.organization_id) {
      headers.set('x-organization-id', config.organization_id);
    }
    if (config.division_id) {
      headers.set('x-division-id', config.division_id);
    }
    if (config.practice_id) {
      headers.set('x-practice-id', config.practice_id);
    }

    const token = jwtToken();
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }

    Object.entries({ ...gaHeaders, ...getGoogleClickIdHeaders() }).forEach(([key, value]) => {
      headers.set(key, value);
    });

    request.headers = headers;

    if (request.data && typeof request.data === 'object' && !(request.data instanceof FormData)) {
      request.data = cleanPayload(request.data);
    }

    // Opt-in request gzip: callers pass `gzipRequest: true` in the axios
    // config and we gzip the serialized JSON body before send. The server
    // must have the matching gzip-request middleware deployed (see
    // dentistry-one-api `src/main.py`). Browsers without CompressionStream
    // (very old) fall back to plain JSON automatically.
    const shouldGzip =
      request.gzipRequest === true &&
      typeof CompressionStream !== 'undefined' &&
      request.data != null &&
      !(request.data instanceof ArrayBuffer) &&
      !(request.data instanceof Blob) &&
      !(request.data instanceof FormData);
    if (shouldGzip) {
      try {
        const json = typeof request.data === 'string' ? request.data : JSON.stringify(request.data);
        const stream = new Blob([json]).stream().pipeThrough(new CompressionStream('gzip'));
        const buf = await new Response(stream).arrayBuffer();
        request.data = buf;
        headers.set('Content-Encoding', 'gzip');
        headers.set('Content-Type', 'application/json');
      } catch (err) {
        console.warn('Request gzip failed; sending uncompressed', err);
      }
    }

    return request;
  },
  (error) => Promise.reject(error),
);

// Mirrors IdleTimer's `jwtTokenExpire` write — a 20h-future client-side marker.
const JWT_EXPIRE_COOKIE_MS = 20 * 60 * 60 * 1000;

// Single-flight guard: a burst of concurrent 401s triggers exactly one refresh
// round-trip, and every waiting request awaits the same promise.
let refreshPromise: Promise<string | null> | null = null;

// Refreshes the access token and persists it to the cookies the request
// interceptor reads, returning the new token — or null when there's no refresh
// token or the refresh fails, in which case the original 401 is surfaced
// unchanged. Uses a bare axios POST, not `client`, so it neither re-enters this
// interceptor nor sends the now-stale Authorization header.
const refreshAccessToken = async (): Promise<string | null> => {
  const currentRefreshToken = Cookies.get('refreshToken');
  if (!currentRefreshToken) {
    return null;
  }
  try {
    const { data } = await axios.post<{ access_token: string }>(`${config.api.baseUrl}/auth/refresh_token`, {
      refresh_token: currentRefreshToken,
    });
    Cookies.set('jwtToken', data.access_token, cookieOptions());
    Cookies.set('jwtTokenExpire', (Date.now() + JWT_EXPIRE_COOKIE_MS).toString(), cookieOptions());
    return data.access_token;
  } catch {
    return null;
  }
};

client.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error?.config as InternalAxiosRequestConfig | undefined;
    const isRefreshCall = typeof original?.url === 'string' && original.url.includes('/auth/refresh_token');

    // A 401 means the access token was rejected — typically because it expired
    // between IdleTimer's 5-minute proactive refreshes (e.g. the interval was
    // suspended while a laptop slept). Transparently refresh the token and
    // replay the request once instead of surfacing the error and forcing the
    // user to log out and back in to mint a fresh token. The refresh endpoint
    // itself is excluded and each request is retried at most once, so a
    // still-failing 401 can't loop.
    if (error?.response?.status === 401 && original && !original._retriedAfterRefresh && !isRefreshCall) {
      original._retriedAfterRefresh = true;
      if (!refreshPromise) {
        refreshPromise = refreshAccessToken().finally(() => {
          refreshPromise = null;
        });
      }
      const newToken = await refreshPromise;
      if (newToken) {
        return client(original);
      }
    }

    console.error('Axios request error:', error);
    return Promise.reject(error);
  },
);